/**
 * @file Spin 加载中
 * @description 参考 Ant Design Spin：可包裹内容，加载时半透明蒙层 + 模糊；也可单独使用。
 *
 * @whenToUse 区块 / 卡片加载、表格区域刷新、需要挡住交互的加载态。
 * @whenNotToUse 覆盖父级的加载指示（用 Spinner）；列表占位（用 Skeleton）。
 */

import type { ReactNode } from 'react'
import { LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/cn.ts'

export type SpinProps = {
  /** 是否处于加载中 */
  spinning?: boolean
  /** 加载文案 */
  tip?: ReactNode
  /** 被包裹的内容；不传则仅渲染指示器 */
  children?: ReactNode
  className?: string
  /** 指示器无障碍文案 */
  label?: string
}

/**
 * 加载中：单独指示器，或对子内容盖蒙层（`backdrop-blur-sm` = 4px 模糊）
 */
export function Spin({
  spinning = true,
  tip,
  children,
  className,
  label = '加载中',
}: SpinProps) {
  const indicator = (
    <div className="flex flex-col items-center justify-center gap-2">
      <span
        role="status"
        aria-label={label}
        className="inline-flex text-primary"
      >
        <LoaderCircle className="size-5 animate-spin" aria-hidden />
      </span>
      {tip ? (
        <div className="text-sm text-muted">{tip}</div>
      ) : null}
    </div>
  )

  if (children === undefined || children === null) {
    return (
      <div
        className={cn(
          'inline-flex items-center justify-center',
          !spinning && 'hidden',
          className,
        )}
      >
        {spinning ? indicator : null}
      </div>
    )
  }

  return (
    <div className={cn('relative overflow-hidden', className)}>
      <div
        className={cn(
          'transition-opacity duration-200',
          spinning && 'pointer-events-none select-none opacity-60',
        )}
        aria-busy={spinning || undefined}
      >
        {children}
      </div>
      {spinning ? (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center rounded-[inherit] bg-raised/50 backdrop-blur-sm transition-opacity duration-200"
          aria-hidden
        >
          {indicator}
        </div>
      ) : null}
    </div>
  )
}
